import { useEffect, useMemo, useState } from 'react'
import { useLocalStorage } from 'react-use'

import { DEFAULT_MATERIAL_TYPOGRAPHY } from '@makyoapp/makyo-tw-ui'

import { useBrandKitDetailContext } from '@/context/BrandKitDetailContext'
import { useCacheContext } from '@/context/CacheContext'
import {
  BrandKit,
  MT_BREAKPOINTS,
  MT_HTML_TAGS,
  MT_SIZE,
  MT_SIZE_OPTIONS,
  MT_TYPE_TYPE,
  MtTypography,
} from '@/shared/brand-kit'
import { STORAGE_KEYS } from '@/shared/constants'

export const useBrandkit = () => {
  const [activeTypography, setActiveTypography] = useLocalStorage(
    STORAGE_KEYS.BRANDKIT_ACTIVE_TYPOGRAPHY,
    MT_TYPE_TYPE[0]
  )

  const [activeSize, setActiveSize] = useLocalStorage(STORAGE_KEYS.BRANDKIT_ACTIVE_SIZE, MT_SIZE[0])

  const { brand } = useBrandKitDetailContext()

  const isNavigationBar = activeTypography === 'NavigationBar'
  const isHTML = activeTypography === 'HTML' || activeTypography === 'RichText' || activeTypography === 'Rich Text'

  useEffect(() => {
    if (activeSize) {
      const isInBreakpoints = MT_BREAKPOINTS.some((breakpoint) => breakpoint.value === activeSize)
      const isInSizes = MT_SIZE_OPTIONS.map((item) => item.label).includes(activeSize)
      const isInHtml = MT_HTML_TAGS.map((item) => item.value).includes(activeSize)

      if ((isNavigationBar && isInBreakpoints) || (isHTML && isInHtml)) return

      if (isNavigationBar && !isInBreakpoints) {
        setActiveSize(MT_BREAKPOINTS[0].value)
        return
      }

      if (isHTML && !isInBreakpoints) {
        setActiveSize(MT_HTML_TAGS[0].value)
        return
      }

      if (!isNavigationBar && !isInSizes) {
        setActiveSize(MT_SIZE_OPTIONS[0].label)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTypography, activeSize])

  const { allFonts } = useCacheContext()

  const [draftBrandKit, setDraftBrandKit] = useState<BrandKit | undefined>(brand)

  const typography = useMemo(() => draftBrandKit?.typography || ({} as MtTypography), [draftBrandKit])

  const baseKey = activeTypography?.toLowerCase()

  const allTypes = [
    ...MT_SIZE_OPTIONS.map((size) => size.name),
    ...MT_HTML_TAGS.map((tag) => tag.label),
    'Phone',
    'PhoneLandscape',
    'Tablet',
    'TabletLandscape',
    'Desktop',
    'Tv',
  ] as const

  const typographyKeys = Object.fromEntries(
    allTypes.map((type) => [
      `${type.charAt(0).toLowerCase()}${type.slice(1)}Key`,
      `${baseKey}${type}` as MtTypographyKey,
    ])
  )

  const { largeKey, phoneKey } = typographyKeys

  const selectedFont = useMemo(
    () =>
      allFonts.find((f) => f.family === typography?.[isNavigationBar ? phoneKey : largeKey]?.familyName) || allFonts[0],
    [allFonts, typography, isNavigationBar, phoneKey, largeKey]
  )

  const variants = useMemo(() => selectedFont?.variants || [], [selectedFont])

  type MtTypographyKey = keyof typeof DEFAULT_MATERIAL_TYPOGRAPHY

  function getTypographyValueSafe(
    key: MtTypographyKey,
    draftBrandKit?: BrandKit | null
  ): (typeof DEFAULT_MATERIAL_TYPOGRAPHY)[MtTypographyKey] {
    if (!draftBrandKit || !draftBrandKit.typography || !draftBrandKit.typography[key]) {
      return DEFAULT_MATERIAL_TYPOGRAPHY[key]
    }
    return draftBrandKit.typography[key]
  }

  return {
    getTypographyValueSafe,
    draftBrandKit,
    setDraftBrandKit,
    activeTypography,
    setActiveTypography,
    activeSize,
    setActiveSize,
    isNavigationBar,
    isHTML,
    variants,
  }
}
